import {
  getCountryInfoApi,
  login,
  loginSocial,
  preregister,
  setCountryInfo,
  signup,
  verifyEmail,
} from 'actions/user';
import {
  Checkbox,
  Form,
  Password,
  Select,
  Submit,
  Text,
} from 'components/utils/form';
import Icon from 'components/utils/Icon';
import Modal from 'components/utils/Modal';
import Tooltip from 'components/utils/Tooltip';
import useResponsive from 'hooks/useResponsive';
import useTranslation, { mergeTranslation } from 'hooks/useTranslation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGoogleLogin } from '@react-oauth/google';
import countries from 'tools/constants/countries';
import { useRouterWithLang } from 'hooks/useRouterWithLang';

import { LANGUAGES, LOGIN_ERRORS } from '../../tools/constants';
import message from '../utils/message';
import SocialButton from '../utils/SocialButton';
import TagManager from 'react-gtm-module';
import { getLocalStorage, setLocalStorage } from '../../tools/localStorage';
import {
  getLocaleTranslation,
  setLangDescription,
  setLocale,
  setTranslation,
} from 'actions/translations';
import { setRedirectUrl } from 'actions/ui';
import { Link } from 'components/utils/Link';
import { createCatalog } from 'actions/channel';

function RegisterForm({ step, setStep, data }) {
  const { isMobile } = useResponsive();
  const [show, setShow] = useState();
  const showPassword = useCallback(() => {
    setShow(!show);
  }, [show]);
  const dispatch = useDispatch();
  const router = useRouterWithLang();
  const [checkedTerms, setCheckedTerms] = useState(false);
  const { locale } = useTranslation();
  const { TRANSLATIONS, translationsParameters } = useTranslation();
  const { redirectUrl } = router.query;
  const countryInfo = useSelector((s) => s.countryInfo);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [socialUser, setSocialUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [skipStep2, setSkipStep2] = useState(false);
  const user = useSelector((state) => state?.user);
  const channelcode = useSelector((state) => state.UI.channelcode);
  const gclid = useSelector((state) => state.UI.gclid);
  const { lang } = router.query;

  useEffect(() => {
    let timeoutRef;
    timeoutRef = setTimeout(() => {
      if (lang) {
        dispatch(
          getLocaleTranslation(lang, (translations) => {
            dispatch(setTranslation(mergeTranslation(translations || {})));
            dispatch(setLocale(lang));
            setLocalStorage(`cachedLocale`, lang);
          })
        );
      }
    }, 500);

    return () => clearTimeout(timeoutRef);
  }, [dispatch, lang]);

  useEffect(() => {
    if (step === 2) {
      document.querySelector('.sidebar').classList.add('hide');
      document.querySelector('.main-wrapper').classList.add('maximize');
    } else {
      document.querySelector('.sidebar').classList.remove('hide');
      document.querySelector('.main-wrapper').classList.remove('maximize');
    }
  }, [step]);

  useEffect(() => {
    const getCountryInfo = async () => {
      let cachedLocale = getLocalStorage('cachedLocale');
      let response = await dispatch(getCountryInfoApi());
      dispatch(setCountryInfo(response.data));
      try {
        if (cachedLocale === null) {
          const language = response?.data?.languages
            .split(',')[0]
            .replace('-', '_');
          if (Object.keys(LANGUAGES).includes(language)) {
            dispatch(setLocale(language));
          } else {
            dispatch(setLocale('it_IT'));
          }
        } else {
          dispatch(setLocale(cachedLocale));
        }
      } catch (e) {
        console.log('getCountryInfo Error', e);
      }
    };
    getCountryInfo().then();
  }, [dispatch]);

  const countryList = useMemo(
    () =>
      countries.map(({ country, id, countrycode }) => ({
        label: country,
        value: id,
        countrycode,
      })),
    []
  );

  const getInitialCountry = useMemo(
    () =>
      countryList.find(
        (country) => country?.countrycode === countryInfo?.country_code
      )?.value,
    [countryInfo?.country_code, countryList]
  );

  useEffect(() => {
    if (redirectUrl) {
      const url = decodeURIComponent(redirectUrl);
      dispatch(setRedirectUrl({ redirectUrl: url }));
    }
  }, [dispatch, redirectUrl]);

  const setLanguageFromCountry = useCallback(
    (locale) => {
      if (Object.keys(LANGUAGES).includes(locale)) {
        dispatch(setLocale(locale));
        setLocalStorage(`cachedLocale`, locale);
        setLocalStorage(`lang_description`, locale);
        dispatch(setLangDescription(locale));
        dispatch(
          getLocaleTranslation(
            locale,
            (translations) => {
              dispatch(setTranslation(mergeTranslation(translations || {})));
            },
            () => {}
          )
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (
      step === 2 &&
      formValues &&
      !hasSignedUp &&
      selectedCountry &&
      skipStep2
    ) {
      setHasSignedUp(true);
      let values = { ...formValues };

      values.privacy1 = values.privacy;
      values.privacy2 = values.privacy;
      values.phone = {
        pref: values.prefix,
        num: values.phoneNumber,
      };
      const country = countries.find((c) => c.id === selectedCountry);
      values.countryCode = country?.countrycode;
      values.countryId = country?.id;
      values.localeCode = country?.localecode || 'en_US';
      values.alreadySell = values.alreadySell === 'Yes';
      values.assistanceRequested = values.assistanceRequested === 'Yes';
      values.avgTurnover = values.avgTurnover || '';
      //   values.assistanceRequested = values.assistanceRequested === 'Yes';
      //   values.avgTurnover = values.avgTurnover || '';
      values.DidYouKnowBDroppy =
        channelcode || data?.DidYouKnowBDroppy // no_profilation
          ? channelcode || data?.DidYouKnowBDroppy
          : 'no_profilation';
      values.gclid = gclid;
      setLanguageFromCountry(values.localeCode);
      dispatch(
        signup(
          {
            ...values,
            lang: Object.keys(LANGUAGES).includes(values.localeCode)
              ? values?.localeCode?.toUpperCase()
              : locale.toUpperCase(),
            limitTarsCount: 1000,
            newsletters: false,
          },
          () => {
            //  dispatch(showNewSignupPopupAction(true));
            dispatch(
              preregister({ ...values, lang: locale.toUpperCase() }, () => {
                dispatch(
                  login(
                    {
                      email: values.email,
                      password: values.password,
                    },
                    (userCatalogs) => {
                      if (userCatalogs?.length === 0) {
                        const currency =
                          countries.find(
                            (country) =>
                              country.countrycode === user?.countrycode
                          )?.currency || 'EUR';
                        dispatch(
                          createCatalog({
                            ids: [],
                            connections: [
                              {
                                service: 'socialEcommerce',
                                component: 'socialEcommerce',
                              },
                            ],
                            name: 'Wishlist',
                            language: Object.keys(LANGUAGES).includes(
                              values.localeCode
                            )
                              ? values?.localeCode
                              : locale,
                            currency,
                            minQuantity: 1,
                            allProducts: true,
                          })
                        );
                      }
                    }
                  )
                );
              })
            );
          }
        )
      );
    }
  }, [
    channelcode,
    data?.DidYouKnowBDroppy,
    dispatch,
    formValues,
    gclid,
    hasSignedUp,
    locale,
    router,
    selectedCountry,
    setLanguageFromCountry,
    skipStep2,
    step,
    user?.countrycode,
  ]);

  const onSubmit = useCallback(
    async (values) => {
      if (step === 1) {
        let response;
        try {
          response = await dispatch(verifyEmail(values.email));
        } catch (e) {
          console.log('Error check email', e);
        }
        if (response?.data?.exists === true) {
          message.error(TRANSLATIONS.auth.alreadyInUse);
        } else {
          values.gclid = gclid;
          await new Promise((resolve) => {
            dispatch(
              preregister(
                {
                  ...values,
                  lang: Object.keys(LANGUAGES).includes(values.localeCode)
                    ? values?.localeCode?.toUpperCase()
                    : locale.toUpperCase(),
                },
                (response) => {
                  if (
                    response?.data?.error ===
                    'Contact Email Address is not valid.'
                  ) {
                    message.error(TRANSLATIONS.auth.invalidEmail);
                  } else {
                    values.privacy1 = values.privacy;
                    values.privacy2 = values.privacy;
                    values.phone = {
                      pref: values.prefix,
                      num: values.phoneNumber,
                    };
                    const country = countries.find(
                      (c) => c.id === values.country
                    );
                    values.countryCode = country?.countrycode;
                    values.countryId = country?.id;
                    values.localeCode = country?.localecode || 'en_US';
                    values.alreadySell = values.alreadySell === 'Yes';
                    values.assistanceRequested =
                      values.assistanceRequested === 'Yes';
                    values.avgTurnover = values.avgTurnover || '';
                    values.DidYouKnowBDroppy =
                      channelcode || data?.DidYouKnowBDroppy // no_profilation
                        ? channelcode || data?.DidYouKnowBDroppy
                        : 'no_profilation';
                    values.gclid = gclid;
                    TagManager.dataLayer({
                      dataLayer: {
                        event: 'registration',
                        authenticationMethod: 'email',
                        userEmail: values?.email?.toLowerCase(),
                        userCountry: values?.countryCode?.toLowerCase(),
                      },
                    });
                    setLanguageFromCountry(values.localeCode);
                    dispatch(
                      signup(
                        {
                          ...values,
                          lang: Object.keys(LANGUAGES).includes(
                            values.localeCode
                          )
                            ? values?.localeCode?.toUpperCase()
                            : locale.toUpperCase(),
                          limitTarsCount: 1000,
                          newsletters: false,
                        },
                        () => {
                          //  dispatch(showNewSignupPopupAction(true));
                          dispatch(
                            preregister(
                              {
                                ...values,
                                lang: Object.keys(LANGUAGES).includes(
                                  values.localeCode
                                )
                                  ? values?.localeCode?.toUpperCase()
                                  : locale.toUpperCase(),
                              },
                              () => {}
                            )
                          );
                          dispatch(
                            login(
                              {
                                email: values.email,
                                password: values.password,
                              },
                              (userCatalogs) => {
                                if (userCatalogs?.length === 0) {
                                  const currency =
                                    countries.find(
                                      (country) =>
                                        country.countrycode === user.countrycode
                                    )?.currency || 'EUR';
                                  dispatch(
                                    createCatalog({
                                      ids: [],
                                      connections: [
                                        {
                                          service: 'socialEcommerce',
                                          component: 'socialEcommerce',
                                        },
                                      ],
                                      name: 'Wishlist',
                                      language: Object.keys(LANGUAGES).includes(
                                        values.localeCode
                                      )
                                        ? values?.localeCode
                                        : locale,
                                      currency,
                                      minQuantity: 1,
                                      allProducts: true,
                                    })
                                  );
                                }
                              }
                            )
                          );
                        }
                      )
                    );
                  }
                  resolve();
                }
              )
            );
          });
        }
      } else if (step === 2) {
        values.privacy1 = values.privacy;
        values.privacy2 = values.privacy;
        values.phone = {
          pref: values.prefix,
          num: values.phoneNumber,
        };
        const country = countries.find((c) => c.id === values.country);
        values.countryCode = country?.countrycode;
        values.countryId = country?.id;
        values.localeCode = country?.localecode || 'en_US';
        values.alreadySell = values.alreadySell === 'Yes';
        values.assistanceRequested = values.assistanceRequested === 'Yes';
        values.avgTurnover = values.avgTurnover || '';
        values.DidYouKnowBDroppy =
          channelcode || data?.DidYouKnowBDroppy // no_profilation
            ? channelcode || data?.DidYouKnowBDroppy
            : 'no_profilation';
        values.gclid = gclid;
        setLanguageFromCountry(values.localeCode);
        dispatch(
          signup(
            {
              ...values,
              lang: Object.keys(LANGUAGES).includes(values.localeCode)
                ? values?.localeCode?.toUpperCase()
                : locale.toUpperCase(),
              limitTarsCount: 1000,
              newsletters: false,
            },
            () => {
              //  dispatch(showNewSignupPopupAction(true));
              dispatch(
                preregister(
                  {
                    ...values,
                    lang: Object.keys(LANGUAGES).includes(values.localeCode)
                      ? values?.localeCode?.toUpperCase()
                      : locale.toUpperCase(),
                  },
                  () => {}
                )
              );
              dispatch(
                login(
                  {
                    email: values.email,
                    password: values.password,
                  },
                  (userCatalogs) => {
                    if (userCatalogs?.length === 0) {
                      const currency =
                        countries.find(
                          (country) => country.countrycode === user.countrycode
                        )?.currency || 'EUR';
                      dispatch(
                        createCatalog({
                          ids: [],
                          connections: [
                            {
                              service: 'socialEcommerce',
                              component: 'socialEcommerce',
                            },
                          ],
                          name: 'Wishlist',
                          language: Object.keys(LANGUAGES).includes(
                            values.localeCode
                          )
                            ? values?.localeCode
                            : locale,
                          currency,
                          minQuantity: 1,
                          allProducts: true,
                        })
                      );
                    }
                  }
                )
              );
            }
          )
        );
      }
    },
    [
      TRANSLATIONS.auth.alreadyInUse,
      TRANSLATIONS.auth.invalidEmail,
      channelcode,
      data?.DidYouKnowBDroppy,
      dispatch,
      gclid,
      locale,
      setLanguageFromCountry,
      step,
      user.countrycode,
    ]
  );

  useEffect(() => {
    if (getInitialCountry) {
      setSkipStep2(true);
      setSelectedCountry(getInitialCountry);
    }
  }, [getInitialCountry]);

  const handleSocialLogin = (user, values) => {
    let _data = {
      type: user.provider,
      token: user.token.accessToken,
    };
    if (socialUser) {
      const country = countries.find((c) => c.id === selectedCountry);
      _data = {
        ..._data,
        countryCode: country?.countrycode,
        countryId: country?.id,
        localeCode: country?.localecode || 'en_US',
        alreadySell: values.alreadySell === 'Yes',
        assistanceRequested: values.assistanceRequested === 'Yes',
        avgTurnover: values.avgTurnover || '',
        phone: {
          pref: values.prefix,
          num: values.phoneNumber,
        },
        website: values.website,
        description: values.description,
        DidYouKnowBDroppy:
          channelcode || data?.DidYouKnowBDroppy // no_profilation
            ? channelcode || data?.DidYouKnowBDroppy
            : 'no_profilation',
        gclid,
        lang: Object.keys(LANGUAGES).includes(_data.localeCode)
          ? _data?.localeCode?.toUpperCase()
          : locale.toUpperCase(),
        limitTarsCount: 1000,
        newsletters: false,
      };
      setLanguageFromCountry(_data?.localeCode);
    }
    dispatch(
      loginSocial(
        _data,
        (response) => {
          if (response?.data?.missingFields) {
            setSocialUser(user);
            setModalOpen(true);
          } else {
            if (response.length === 0) {
              const currency =
                countries.find(
                  (country) => country.countrycode === user.countrycode
                )?.currency || 'EUR';
              dispatch(
                createCatalog({
                  ids: [],
                  connections: [
                    {
                      service: 'socialEcommerce',
                      component: 'socialEcommerce',
                    },
                  ],
                  name: 'Wishlist',
                  language: Object.keys(LANGUAGES).includes(_data.localeCode)
                    ? _data?.localeCode?.toUpperCase()
                    : locale.toUpperCase(),
                  currency,
                  minQuantity: 1,
                  allProducts: true,
                })
              );
            }
          }
        },
        (errorType) => {
          if (errorType === LOGIN_ERRORS.CREDENTIALS) {
            message.error(TRANSLATIONS.auth.wrongCredentials, {
              duration: 5,
            });
          }
          if (errorType === LOGIN_ERRORS.ROLE_EXPIRED) {
            message.error(TRANSLATIONS.auth.roleExpired, { duration: 5 });
          }
          if (errorType === LOGIN_ERRORS.NO_EMAIL_FROM_SOCIAL) {
            message.error(TRANSLATIONS.auth.noEmailFromSocial, {
              duration: 10,
            });
          }
        },
        !!socialUser
      )
    );
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      handleSocialLogin({
        provider: 'google',
        token: { accessToken: codeResponse?.access_token },
      });
    },
    onError: (err) => {
      handleSocialLoginFailure(err);
    },
    flow: 'implicit',
  });

  const handleSocialLoginFailure = useCallback((err) => {
    if (err?.message?.includes('google')) {
      if (!err?.message?.includes('popup_closed_by_user')) {
        if (
          err.message.includes(
            'Cookies are not enabled in current environment.'
          )
        ) {
          setGoogleError('enableCookies');
        } else {
          setGoogleError('genericError');
        }
      }
    }
  }, []);

  return (
    <div className="register-form">
      <Modal
        openModal={modalOpen}
        onClose={() => {
          router.reload(window.location.pathname);
        }}
        width={isMobile ? '100%' : '750px'}
        scroll={false}
      >
        <div className="jc-c register-modal g-20 fd-c d-f">
          <h2>{TRANSLATIONS.auth.welcome}</h2>
          <div>
            <p>{TRANSLATIONS.auth.fewQuestion}</p>
          </div>
          <Form onSubmit={(_data) => handleSocialLogin(socialUser, _data)}>
            <div className="mb-20">
              <h4>{TRANSLATIONS.auth.selectCountry}</h4>
              <div className="country">
                <Select
                  name="country"
                  placeholder={TRANSLATIONS.auth.selectCountry}
                  options={countryList}
                  clearable={false}
                  defaultValue={selectedCountry}
                  onChange={setSelectedCountry}
                  required
                />
              </div>
            </div>
            <Submit
              label={TRANSLATIONS.auth.completeSocialRegistration}
              className="large button primary wide"
              disableOnValidation
            ></Submit>
          </Form>
        </div>
      </Modal>
      <div className={step !== 2 ? '' : 'card'}>
        <Form onSubmit={onSubmit} initialValues={data} onChange={setFormValues}>
          <div
            style={{ display: step !== 1 && 'none' }}
            className="d-f g-10 fd-c"
          >
            <Text
              name="name"
              placeholder={TRANSLATIONS.auth.name}
              className="large"
              required
            />
            <Text
              noCapitalize
              name="email"
              placeholder="Email"
              className="large"
              required
            />
            <Password
              name="password"
              placeholder="Password"
              className="large"
              type={show ? 'text' : 'password'}
              endAdornment={
                <Icon
                  onClick={() => showPassword()}
                  type={show ? 'eye-off' : 'eye'}
                />
              }
              required
              autoComplete={'new-password'}
            />
            <Select
              name="country"
              placeholder={TRANSLATIONS.auth.selectCountry}
              options={countryList}
              required
              clearable={false}
              defaultValue={selectedCountry}
              onChange={setSelectedCountry}
            />
            <div className={'d-f mt-md mb-l link'}>
              <Checkbox
                required
                type="checkbox"
                name={`privacy`}
                className="large"
                checked={checkedTerms}
                onChange={() => {
                  setCheckedTerms((prev) => !prev);
                }}
              />
              <div className={'mrl-md'}>
                {translationsParameters(TRANSLATIONS.auth.privacy, [
                  <Link href={'/terms-and-conditions'} key={'terms'}>
                    <a>{TRANSLATIONS.auth.privacy1}</a>
                  </Link>,
                  <Link href={'/privacy-policy'} key={'privacy'}>
                    <a> {TRANSLATIONS.auth.privacy2}</a>
                  </Link>,
                ])}
              </div>
            </div>
          </div>
          <Submit
            label={TRANSLATIONS.auth.create}
            className="large button primary wide"
            disableOnValidation
            disabled={step === 1 && !checkedTerms}
          />
          <div className="link-wrapper">
            <span>{TRANSLATIONS.auth.already}</span>
            <Link href="/login">{TRANSLATIONS.auth.loginto}</Link>
          </div>
        </Form>
        {step === 1 && !data && (
          <>
            <div className="divider"></div>
            <div className="social-buttons-container">
              {googleError && (
                <Tooltip
                  className={'bottom'}
                  containerClassName={'container-tooltip'}
                  text={
                    googleError === 'enableCookies'
                      ? TRANSLATIONS.auth.enableCookies
                      : TRANSLATIONS.auth.genericGoogleError
                  }
                >
                  <button className="social-button disabled-social-button">
                    <div className="box-image">
                      <img
                        src="/images/social-network/google.svg"
                        alt="google"
                      />
                    </div>
                    {TRANSLATIONS.auth.googleLogin}
                  </button>
                </Tooltip>
              )}
              <button className="social-button" onClick={googleLogin}>
                <div className="box-image">
                  <img src="/images/social-network/google.svg" alt="google" />
                </div>
                <p>{TRANSLATIONS.auth.googleSignup}</p>
              </button>
              <SocialButton
                provider="facebook"
                appId={process.env.FACEBOOK_APP_ID}
                onLoginSuccess={handleSocialLogin}
                onLoginFailure={handleSocialLoginFailure}
                className="social-button "
                scope={['email']}
              >
                <div className="box-image">
                  <img
                    src="/images/social-network/facebook.svg"
                    alt="facebook"
                  />
                </div>
                <p>{TRANSLATIONS.auth.facebookSignup}</p>
              </SocialButton>
            </div>
          </>
        )}
      </div>
      <style>
        {`
        .label{
          margin-bottom:0px!important;
        }
        .hide{
          display:none;
        }
        .registration-form{
          max-width:585px;
        }
        .registration-form h4{
          margin-top:8px;
        }
        .registration-form h6{
          margin-top:8px;
        }
        .register-modal{
          text-align:left;
        }
        .visible{
          opacity:1;
          transition:all .4s;
          transform:translateX(0px);
        }
        .error{
          border-color:red;
        }
        .form-links {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1em;
          font-size: 13px;
          font-weight: 500;
          line-height: 25px;
        }
        .link a{
          color:var(--primary);
          font-weight:600;
          cursor:pointer;
          font-size:13px;
        }
        .link{
          font-size:13px;
        }
        .registration-form h4{
          font-weight:700;
          margin-bottom:8px;
          font-size:20px;
          color:var(--foreground);
        }
        .registration-form h6{
          font-weight:600;
          margin-bottom:8px;
          font-size:18px;
          color:var(--foreground);
        }
        .registration-form h6 span{
          font-weight:400;
          margin-bottom:8px;
          font-size:14px;
          color:var(--grey-light);
        }
        .discover, .sell, .country{
          margin:8px 0px;
        }
        .discover .radio-group label, .sell .radio-group label{
          margin:8px 0px;
          display: flex;
          align-items: center;
          cursor:pointer;
          font-size:14px;
        }
        .discover .radio-group input, .sell .radio-group input{
          margin-right:16px;
        }
        
        .mobile .options{
          margin:8px 0px;
          display:flex;
          justify-content:space-between;
        }
        .mobile .options > div:first-child{
          flex:2;
        }
        .mobile .options > div{
          flex:3;
        }
        @media all and (min-width: 992px){
          .hide{
            visibility:hidden;
            opacity:0;
            display:block;
            height:0;
            transform:translateX(-50px);
          }
          .maximize .content{
            justify-content:center;
            background:var(--background-utils);
            padding:0;
            margin-top:25px;
            border-radius:8px;
          }
        }
        .maximize{
          width:100%;
          max-width:100%!important;
        }
        .social-button {
          height: 50px;
          border: none;
          border-radius: 5px;
          font-size: 16px;
          text-align:center;
          text-decoration: none;
          background-color: var(--white);
          color: var(--grey);
          cursor: pointer;
          border: 1px solid #e7e7e7;
          transition:all .3s ease;
        }
        .social-button:hover {
          box-shadow:var(--shadow-light-hover);
          transform: translateY(-1px);
        }
        .social-button p{
          font-weight:500;
        }
        .divider{
          margin-bottom: 15px;
          margin-top:35px;
          padding: 0;
          overflow: visible;
          border: none;
          border-top: 1px solid var(--grey-lighter);
          color: var(--grey-lighter);
          text-align: center;
        }
        .divider:after{
          content: 'Or';
          display: inline-block;
          position: relative;
          top: -7px;
          padding: 0 16px; 
          background: var(--background);    
        }
        .social-buttons-container {
          display: flex;
          flex-direction: column;
          gap:15px;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 10px;
          text-align:center;
        }
        .box-image{
          float:left;
          width:20px;
          height:20px;
          left:10px;
          position:relative;
        }
        .modal-overflow .modal{
          overflow: hidden scroll;
        }
        @media only screen and (max-width: 425px) {
          .social-button {
            width: 100%;
          }
          .facebook-button {
            margin-top: 15px;
          }
          .modal-overflow .modal{
            overflow:scroll!important;
            max-height:100%!important;
          }
          .modal h2{
            font-size:22px;
          }
          .modal h4{
            font-size:18px;
          }
          .register-modal{
            padding:20px 0px 50px;
          }
        }
        .hide-button {
          display: none;
        }
        .disabled-social-button {
          border: 1px solid var(--disabled);
          background: var(--disable-bg);
          color: var(--disabled);
          cursor: default;
          width: 100%;
        }
        .container-tooltip {
          width: 100%;
        }  
      `}
      </style>
    </div>
  );
}

export default RegisterForm;
